"use client";

import {
  Award,
  CheckCircle2,
  Crown,
  Handshake,
  Link2,
  Medal,
  TrendingUp,
  Trophy,
  Users,
  Wallet,
} from "lucide-react";
import Link from "next/link";
import { Button } from "@/components/ui/Button";
import { Container } from "@/components/ui/Container";
import {
  useActivatePartner,
  useEligibility,
  usePartnerLeaderboard,
  usePartnerSettings,
} from "@/hooks/usePartners";
import type { LeaderboardEntry } from "@/lib/partner-api";
import { useAuthStore } from "@/store/auth";

function formatPKR(amount: string): string {
  return `PKR ${Number(amount).toLocaleString()}`;
}

const RANK_ICONS = [
  { icon: Crown, className: "text-gold-500" },
  { icon: Medal, className: "text-neutral-400" },
  { icon: Medal, className: "text-amber-700" },
];

function LeaderboardColumn({
  title,
  entries,
  testId,
}: {
  title: string;
  entries: LeaderboardEntry[];
  testId: string;
}) {
  return (
    <div data-testid={testId}>
      <h3 className="text-ink-900 mb-3 text-sm font-semibold">{title}</h3>
      {entries.length === 0 ? (
        <p className="text-ink-400 py-6 text-center text-xs">
          No earnings yet — be the first!
        </p>
      ) : (
        <ul className="space-y-2">
          {entries.map((entry) => {
            const rankIcon = RANK_ICONS[entry.rank - 1];
            const Icon = rankIcon?.icon;
            return (
              <li
                key={entry.rank}
                className="flex items-center gap-3 rounded-md border border-neutral-200 bg-white px-3 py-2"
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center">
                  {Icon ? (
                    <Icon className={`h-5 w-5 ${rankIcon.className}`} />
                  ) : (
                    <span className="text-ink-500 text-sm font-semibold">
                      {entry.rank}
                    </span>
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-ink-900 truncate text-sm font-medium">
                    {entry.name}
                  </p>
                </div>
                <span className="text-ink-700 shrink-0 text-sm font-semibold">
                  {formatPKR(entry.amount)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function PartnerPage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const settingsQuery = usePartnerSettings();
  const eligibilityQuery = useEligibility();
  const activate = useActivatePartner();
  const leaderboardQuery = usePartnerLeaderboard();

  const settings = settingsQuery.data;
  const eligibility = eligibilityQuery.data;
  const leaderboard = leaderboardQuery.data;
  const threshold =
    eligibility?.threshold ?? settings?.eligibility_threshold ?? 0;
  const current = eligibility?.current ?? 0;
  const progress =
    threshold > 0 ? Math.min(100, Math.round((current / threshold) * 100)) : 0;
  const isPartner = eligibility?.is_partner ?? false;
  const isEligible = eligibility?.eligible ?? false;

  function handleActivate() {
    activate.mutate();
  }

  return (
    <Container className="py-12" data-testid="partner-page">
      <div className="mx-auto max-w-4xl space-y-10">
        {/* Hero */}
        <div className="text-center">
          <span className="bg-gold-50 text-gold-700 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium">
            <Handshake className="h-3.5 w-3.5" />
            Partner Program
          </span>
          <h1 className="text-ink-900 mt-4 text-3xl font-semibold sm:text-4xl">
            Become a Zeylora Partner
          </h1>
          <p className="text-ink-500 mx-auto mt-3 max-w-2xl text-sm sm:text-base">
            Earn commissions on every referral, build your downline, and grow a
            sustainable income stream by sharing the jewellery you love.
          </p>
        </div>

        {/* Leaderboard */}
        <div
          className="border-ink-100 bg-white rounded-lg border p-6"
          data-testid="partner-leaderboard"
        >
          <h2 className="text-ink-900 mb-4 flex items-center gap-2 text-lg font-semibold">
            <Trophy className="text-gold-500 h-5 w-5" />
            Top Partners
          </h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <LeaderboardColumn
              title="Last Month"
              entries={leaderboard?.last_month ?? []}
              testId="partner-leaderboard-last-month"
            />
            <LeaderboardColumn
              title="Lifetime"
              entries={leaderboard?.lifetime ?? []}
              testId="partner-leaderboard-lifetime"
            />
          </div>
        </div>

        {/* Motivational text */}
        <div
          className="bg-gold-50 rounded-lg p-5 text-center"
          data-testid="partner-motivational"
        >
          {!isAuthenticated ? (
            <p className="text-ink-700 text-sm">
              These partners are already earning commissions. Sign up, start
              shopping, and your name could be next on this leaderboard!
            </p>
          ) : isPartner ? (
            <p className="text-ink-700 text-sm">
              Keep sharing your referral link and climbing the ranks. Visit your
              dashboard to track your progress and see how close you are to the
              top.
            </p>
          ) : (
            <p className="text-ink-700 text-sm">
              You are on your way! Complete your eligibility progress below and
              your name could be on this leaderboard soon.
            </p>
          )}
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="border-ink-100 bg-white rounded-lg border p-5">
            <Wallet className="text-gold-500 h-6 w-6" />
            <h3 className="text-ink-900 mt-3 text-sm font-semibold">
              Earn Commissions
            </h3>
            <p className="text-ink-500 mt-1 text-sm">
              Get paid a commission on every qualifying order placed through
              your referral link.
            </p>
          </div>
          <div className="border-ink-100 bg-white rounded-lg border p-5">
            <Users className="text-gold-500 h-6 w-6" />
            <h3 className="text-ink-900 mt-3 text-sm font-semibold">
              Build Your Downline
            </h3>
            <p className="text-ink-500 mt-1 text-sm">
              Recruit partners into your chain and earn commissions across
              multiple levels.
            </p>
          </div>
          <div className="border-ink-100 bg-white rounded-lg border p-5">
            <Link2 className="text-gold-500 h-6 w-6" />
            <h3 className="text-ink-900 mt-3 text-sm font-semibold">
              Share Your Link
            </h3>
            <p className="text-ink-500 mt-1 text-sm">
              A single shareable link tracks every referral back to you
              automatically.
            </p>
          </div>
        </div>

        {/* CTA */}
        {!isAuthenticated ? (
          <div className="border-ink-100 bg-gold-50 rounded-lg border p-6 text-center sm:p-10">
            <Award className="text-gold-500 mx-auto h-10 w-10" />
            <h2 className="text-ink-900 mt-3 text-lg font-semibold">
              Join the program today
            </h2>
            <p className="text-ink-500 mx-auto mt-1 max-w-md text-sm">
              Create an account to start tracking your eligibility and unlock
              your partner dashboard.
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              <Link href="/signup">
                <Button size="lg" data-testid="partner-cta-signup">
                  Create account
                </Button>
              </Link>
              <Link href="/login">
                <Button
                  size="lg"
                  variant="outline"
                  data-testid="partner-cta-login"
                >
                  Sign in
                </Button>
              </Link>
            </div>
          </div>
        ) : isPartner ? (
          <div className="border-ink-100 bg-white rounded-lg border p-6 sm:p-10">
            <div className="flex flex-col items-center gap-4 text-center">
              <CheckCircle2 className="text-gold-500 h-10 w-10" />
              <div>
                <h2 className="text-ink-900 text-lg font-semibold">
                  Partner account active
                </h2>
                <p className="text-ink-500 mt-1 text-sm">
                  Your partner dashboard has your referral link, wallet, and
                  downline tree.
                </p>
              </div>
              <Link
                href="/partner/dashboard"
                data-testid="partner-goto-dashboard"
              >
                <Button size="lg">
                  <TrendingUp className="h-4 w-4" />
                  Go to dashboard
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="border-ink-100 bg-white rounded-lg border p-6 sm:p-10">
            <h2 className="text-ink-900 text-lg font-semibold">
              Eligibility progress
            </h2>
            <p className="text-ink-500 mt-1 text-sm">
              {settings?.eligibility_type === "value"
                ? "Reach the spending threshold to unlock the partner program."
                : "Complete the required number of qualifying orders to unlock the partner program."}
            </p>

            <div className="mt-5">
              <div className="text-ink-700 mb-1.5 flex items-center justify-between text-sm font-medium">
                <span>
                  {settings?.eligibility_type === "value" ? "Spent" : "Orders"}
                </span>
                <span>
                  {current.toLocaleString()} / {threshold.toLocaleString()}
                </span>
              </div>
              <div
                className="bg-ink-100 h-2.5 w-full overflow-hidden rounded-full"
                data-testid="partner-eligibility-bar-track"
              >
                <div
                  className="bg-gold-500 h-full rounded-full transition-all"
                  style={{ width: `${progress}%` }}
                  data-testid="partner-eligibility-bar-fill"
                />
              </div>
            </div>

            {isEligible ? (
              <div className="mt-6 flex flex-col items-center gap-3 rounded-md border border-gold-200 bg-gold-50 p-5 text-center">
                <CheckCircle2 className="text-gold-600 h-8 w-8" />
                <div>
                  <h3 className="text-ink-900 text-sm font-semibold">
                    Eligibility unlocked!
                  </h3>
                  <p className="text-ink-500 mt-1 text-sm">
                    Activate your partner account to start earning commissions.
                  </p>
                </div>
                <Button
                  size="lg"
                  onClick={handleActivate}
                  isLoading={activate.isPending}
                  data-testid="partner-activate-btn"
                >
                  <Handshake className="h-4 w-4" />
                  Become a Partner
                </Button>
                {activate.isError && (
                  <p className="text-danger text-xs">
                    {(activate.error as Error)?.message ||
                      "Failed to activate. Please try again."}
                  </p>
                )}
              </div>
            ) : (
              <p
                className="text-ink-500 mt-4 text-center text-xs"
                data-testid="partner-eligibility-pending"
              >
                Keep going — you are {progress}% of the way to becoming a
                partner.
              </p>
            )}
          </div>
        )}
      </div>
    </Container>
  );
}
